import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import { PINNotConfigured } from '@flowwai-frontend/shared-web/iam/ui'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { DEFAULT_DATE_FORMAT } from '@flowwai-frontend/shared/constants'
import { getDateFromDateObject } from '@flowwai-frontend/shared/utils/common'

import * as Styles from './styles'

interface Props {
   isPINConfigured: boolean
   generatedOn: Date | null | undefined
   expiresOn: Date | null | undefined
   onResetPIN: () => void

   containerClassName?: string
   showTitle?: boolean
}

const UserPIN = (props: Props): React.ReactElement => {
   const {
      isPINConfigured,
      generatedOn,
      expiresOn,
      onResetPIN,
      showTitle = true
   } = props

   const t = useCustomT('iam:userPIN')

   const formattedGeneratedOn = generatedOn
      ? getDateFromDateObject(generatedOn, DEFAULT_DATE_FORMAT)
      : ''

   const formattedExpiresOn = expiresOn
      ? getDateFromDateObject(expiresOn, DEFAULT_DATE_FORMAT)
      : ''

   const renderResetPINButton = () => (
      <Button
         hierarchy={Hierarchy.Link}
         size={'ExtraSmall'}
         subVariant={SubVariant.Primary}
         onClick={onResetPIN}
      >
         {t('resetPIN')}
      </Button>
   )

   const renderField = (label: string, value: string) => (
      <div className={Styles.fieldRowStyles}>
         <p className={cn(Styles.fieldLabelStyles, 'text-gray-600')}>{label}</p>
         <p className={cn(Styles.fieldValueStyles, 'text-gray-700')}>{value}</p>
      </div>
   )

   if (!isPINConfigured) {
      return <PINNotConfigured onSetPIN={onResetPIN} />
   }

   return (
      <div
         className={cn(
            Styles.containerStyles,
            props.containerClassName,
            'bg-white'
         )}
      >
         {showTitle ? (
            <p className={cn(Styles.titleStyles, 'text-gray-800')}>
               {t('title')}
            </p>
         ) : null}

         <div className={Styles.fieldsContainerStyles}>
            {renderField(t('generatedOn'), formattedGeneratedOn)}
            <div className={cn(Styles.dividerStyles, 'bg-gray-300')} />
            {renderField(t('expiresOn'), formattedExpiresOn)}
         </div>
         <div className={Styles.buttonContainerStyles}>
            {renderResetPINButton()}
         </div>
      </div>
   )
}

export default observer(UserPIN)
